import { Translator } from '@nzyme/i18n/Translator.js';
import { defineService } from '@nzyme/ioc/Service.js';
import { Logger } from '@nzyme/logging/Logger.js';
import { createPromise } from '@nzyme/utils/createPromise.js';
import AwsS3 from '@uppy/aws-s3';
import Uppy from '@uppy/core';
import { isPatientFileContentTypeValid, MAX_PATIENT_FILE_SIZE, PATIENT_FILE_TYPES, } from '@sigma/files-common/patientFileValidation.js';
import * as l from './FileUploader.loc.js';
import { FileUploadUrlProvider } from './FileUploadUrlProvider.js';
/**
 * Shared file upload service.
 * Uses Uppy + S3 presigned URLs obtained from the app-specific FileUploadUrlProvider.
 */
export const FileUploader = defineService({
    name: 'FileUploader',
    deps: {
        getUploadUrl: FileUploadUrlProvider,
        translator: Translator,
        logger: Logger,
    },
    setup({ getUploadUrl, translator, logger }) {
        return {
            upload,
        };
        async function upload(file, uploadOptions) {
            if (file.id) {
                logger.info('File already uploaded, skipping', { fileId: file.id });
                return;
            }
            file.error = undefined;
            logger.info('Starting file upload', {
                fileType: file.type,
                fileSize: file.file.size,
                chatId: uploadOptions.chatId,
                patientId: uploadOptions.patientId,
            });
            if (file.file.size > MAX_PATIENT_FILE_SIZE) {
                logger.warn('File size exceeds maximum allowed size', {
                    fileSize: file.file.size,
                    maxSize: MAX_PATIENT_FILE_SIZE,
                });
                file.error = translator(l.fileTooLarge);
                return;
            }
            if (!isPatientFileContentTypeValid(file.file.type)) {
                logger.warn('Invalid file content type', { fileType: file.file.type });
                file.error = translator(l.invalidFileType);
                return;
            }
            // Bail out early if already aborted
            if (uploadOptions.signal?.aborted) {
                return;
            }
            const promise = createPromise();
            let uppy;
            try {
                const { fileId, uploadUrl, headers } = await getUploadUrl({
                    chatId: uploadOptions.chatId,
                    patientId: uploadOptions.patientId,
                    name: file.file.name,
                    contentType: file.file.type,
                    size: file.file.size,
                    width: file.width,
                    height: file.height,
                });
                // Check again after async API call
                if (uploadOptions.signal?.aborted) {
                    return;
                }
                file.id = fileId;
                file.progress = 0;
                uppy = new Uppy({
                    autoProceed: true,
                    restrictions: {
                        maxFileSize: MAX_PATIENT_FILE_SIZE,
                        allowedFileTypes: ['image/*', ...PATIENT_FILE_TYPES],
                    },
                });
                uppy.use(AwsS3, {
                    shouldUseMultipart: false,
                    getUploadParameters: () => {
                        return Promise.resolve({
                            method: 'PUT',
                            url: uploadUrl,
                            headers,
                        });
                    },
                });
                uppy.on('upload-progress', (_uppyFile, progress) => {
                    if (_uppyFile && progress.bytesTotal) {
                        file.progress = Math.round((progress.bytesUploaded / progress.bytesTotal) * 100);
                    }
                });
                uppy.on('upload-success', () => {
                    file.progress = undefined;
                    logger.info('File uploaded successfully', { fileId: file.id });
                    promise.resolve();
                });
                uppy.on('upload-error', (_uppyFile, error) => {
                    logger.error('Upload error', { fileId: file.id, error });
                    file.error = translator(l.uploadFailed);
                    promise.reject(error);
                });
                // Cancel the Uppy upload if the signal is aborted
                if (uploadOptions.signal) {
                    const onAbort = () => {
                        logger.info('Upload aborted', { fileId: file.id });
                        uppy?.cancelAll();
                        promise.resolve();
                    };
                    uploadOptions.signal.addEventListener('abort', onAbort, { once: true });
                }
                uppy.addFile({
                    name: file.file.name,
                    type: file.file.type,
                    data: file.file,
                });
                await promise.promise;
            }
            catch (error) {
                logger.error('Failed to upload file', { fileId: file.id, error });
                file.error = translator(l.uploadFailed);
                throw error;
            }
            finally {
                uppy?.destroy();
            }
        }
    },
});
//# sourceMappingURL=FileUploader.js.map